import type { MouseEvent, ReactNode } from "react";
import {
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
  WidgetRowRail,
} from "@salestrak/ui";

import { copyText, openHref } from "#app/lib/utils/copy-actions";

/**
 * One action on a widget row's hover rail: the table's name-cell action —
 * a squared `outline` button on the 28px `icon-sm` rung, icon only, named by
 * its tooltip. The press stops at the button so the row's own press (open in
 * place) never fires with it.
 */
export function RowAction({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-sm"
            variant="outline"
            shape="square"
            aria-label={label}
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              onClick();
            }}
          />
        }
      >
        <Icon name={icon} color="muted" />
      </TooltipTrigger>
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * A widget row's hover rail: the two link verbs every entity menu offers —
 * Open in new tab and Copy link for the row's `href` — after any actions the
 * row adds of its own (`children`, e.g. a queue row's Update). Revealed on
 * hover, or by a held press on a coarse pointer, at the row's end; the Recent
 * card and the Follow ups card share it so a row's verbs read one way.
 */
export function RowLinkActions({ href, children }: { href?: string | undefined; children?: ReactNode }) {
  return (
    <WidgetRowRail>
      {children}
      {href ? (
        <>
          <RowAction icon="external-link" label="Open in new tab" onClick={() => openHref(href)} />
          <RowAction icon="link" label="Copy link" onClick={() => void copyText(href, "Link")} />
        </>
      ) : null}
    </WidgetRowRail>
  );
}
